import { useTranslation } from 'react-i18next';
import InteractiveCard from '../../common/interactiveCard';
import { Clock, Dashboard, DollarCircle } from '../../icons';
import CardData from '../cardData';
import { ITransactionTypes } from '../../../interface/transactionTypes';
import { useLanguage } from '../../../context/LanguageContext';
import { TransactionTypeUsage } from '../../../constants/transaction';
import { useTransaction } from '../../../context/TransactionContext';
import { useTransactionParameters } from '../../../context/TransactionParameterContext';
import { formatter } from '../../../constants/utilityFunctions';
import {
  getTransactionTypePriceKind,
  TransactionTypePriceKind,
} from '../../../constants/transactionTypes';
import { getTransactionTypeSubject } from './transactionTypeSelection';
import { Divider } from '../../Ant';
import { Flex } from 'antd';
import { BorderColor, TagColor } from '../../../interface/common';

const AddTransactionCard = ({
  transactionType,
  onClick,
  hidden,
}: {
  transactionType: ITransactionTypes;
  onClick: (type: ITransactionTypes, subject: string) => void;
  hidden: boolean;
}) => {
  const { t } = useTranslation();
  const { transaction } = useTransaction();
  const { parameters } = useTransactionParameters();
  const { getAppLanguageCode } = useLanguage();
  const transactionTypeName =
    transactionType?.translations?.find((translation) =>
      translation.language.startsWith(getAppLanguageCode()),
    )?.nameTranslation ??
    transactionType?.translations?.find((translation) => translation)?.nameTranslation ??
    '-';
  const priceKind = getTransactionTypePriceKind(transactionType);
  const isHourPrice = priceKind === TransactionTypePriceKind.HOUR;
  const currency = transaction?.matter?.currency ?? parameters?.currency ?? 'EUR';
  const formatPrice = (price?: number) =>
    formatter.regionalNumberFormat({ value: price ?? 0, minimumFractionDigits: 2 });

  return (
    <InteractiveCard
      className={`w-full pointer ${transactionType?.transactionTypeUsage === TransactionTypeUsage.billableMatter ? BorderColor.BEIGE : BorderColor.YELLOW} ${transaction?.transactionType?.id === transactionType.id ? 'transaction-active-card' : ''}`}
      onActivate={() => {
        onClick(
          transactionType,
          getTransactionTypeSubject(
            transactionType?.translations,
            transaction?.matter?.language,
            getAppLanguageCode(),
          ),
        );
      }}
      data-testid={`add-transaction-card-${transactionType.id}`}
    >
      <Flex justify="space-between" align="center" className="pb-10">
        <Flex gap={5} align="center" data-testid="transaction-type-chip">
          <div
            className={`status-chip d-flex justify-center items-center ${transactionType?.transactionTypeUsage === TransactionTypeUsage.billableMatter ? TagColor.BEIGE : TagColor.YELLOW}`}
          >
            {transactionType?.transactionTypeUsage &&
              t(
                `matter:${Object.keys(TransactionTypeUsage).find((key) => TransactionTypeUsage[key as keyof typeof TransactionTypeUsage] === transactionType?.transactionTypeUsage) ?? transactionType?.transactionTypeUsage}`,
              )}
          </div>
        </Flex>
      </Flex>
      <Divider className="mb-10" />
      <Flex vertical gap={10}>
        <CardData
          icon={<Dashboard />}
          type="type"
          title={t('transaction:transactionUnitPriceSource.transactionType')}
          content={transactionTypeName}
          expandable
        />
        {priceKind === TransactionTypePriceKind.STANDARD ? (
          !hidden && (
            <CardData
              icon={<DollarCircle />}
              type="standardPrice"
              title={`${t('transaction:transactionType.standardPrice')} (${currency})`}
              content={formatPrice(transactionType?.standardPrice)}
            />
          )
        ) : (
          <Flex justify="space-between">
            <CardData
              icon={<Clock />}
              type="billableHours"
              title={isHourPrice ? t('transaction:billableHours') : t('transaction:billableAmount')}
              content={formatter.regionalNumberFormat({ value: transactionType?.units ?? 0 })}
            />
            {!hidden && (
              <CardData
                icon={<DollarCircle />}
                type="hourPrice"
                title={`${isHourPrice ? t('transaction:hourPrice') : t('transaction:unitPrice')} (${currency})`}
                content={formatPrice(transactionType?.unitPrice)}
              />
            )}
          </Flex>
        )}
      </Flex>
    </InteractiveCard>
  );
};

export default AddTransactionCard;
